import React from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Sparkles, Coins, LogOut, Menu } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/i18n";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export default function Header() {
  const { user, logout } = useAuth();
  const { lang, setLang, t } = useLang();
  const nav = useNavigate();
  const loc = useLocation();

  const LangToggle = ({ idSuffix = "" }) => (
    <div className="flex rounded-full border-2 border-stone-200 overflow-hidden text-sm font-semibold" data-testid={`lang-toggle${idSuffix}`}>
      {["fr", "en"].map((l) => (
        <button key={l} onClick={() => setLang(l)} data-testid={`lang-${l}${idSuffix}`}
          className={`px-3 py-1.5 transition-colors ${lang === l ? "bg-primary text-white" : "bg-white text-stone-500"}`}>
          {l.toUpperCase()}
        </button>
      ))}
    </div>
  );

  const links = user ? [
    { to: "/earn", label: t("nav_earn") },
    { to: "/video", label: t("nav_video") },
    { to: "/create", label: t("nav_create") },
    { to: "/studio", label: t("nav_studio") },
    { to: "/pricing", label: t("nav_pricing") },
  ] : [
    { to: "/academy", label: t("nav_academy") },
    { to: "/communaute", label: t("nav_community") },
    { to: "/faq", label: t("nav_faq") },
    { to: "/pricing", label: t("nav_pricing") },
  ];

  return (
    <header className="sticky top-0 z-50 bg-[#FAFAF8]/90 backdrop-blur-md border-b-2 border-stone-200">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 h-[72px] flex items-center justify-between gap-3">
        <Link to={user ? "/earn" : "/"} className="flex items-center gap-2 shrink-0" data-testid="logo">
          <img src="/betool-logo.png" alt="Betool" className="w-10 h-10 rounded-2xl shadow-[0_4px_14px_0_rgba(124,58,237,0.4)] object-cover" />
          <span className="font-head font-black text-xl sm:text-2xl tracking-tight">Betool</span>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {links.map((l) => (
            <Link key={l.to} to={l.to} data-testid={`nav-${l.to.slice(1)}`}
              className={`px-4 py-2 rounded-full font-semibold transition-colors ${loc.pathname === l.to ? "bg-primary/10 text-primary" : "text-stone-600 hover:bg-stone-100"}`}>
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2 sm:gap-3">
          <div className="hidden lg:block"><LangToggle /></div>
          {user ? (
            <>
              <Link to="/credits" data-testid="credits-pill"
                className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-secondary text-secondary-foreground font-bold">
                <Coins size={18} /> {user.credits}
              </Link>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button data-testid="user-menu" className="w-11 h-11 rounded-full bg-primary text-white font-bold grid place-items-center">
                    {(user.first_name || user.email)[0].toUpperCase()}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="rounded-2xl">
                  <DropdownMenuItem onClick={() => nav("/profile")} data-testid="menu-profile">{t("nav_profile")}</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => nav("/media")} data-testid="menu-media">{t("nav_media")}</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => nav("/credits")}>{t("nav_credits")}</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => nav("/affiliate")}>{t("nav_affiliate")}</DropdownMenuItem>
                  {user.role === "admin" && <DropdownMenuItem onClick={() => nav("/admin")} data-testid="menu-admin">{t("nav_admin")}</DropdownMenuItem>}
                  <DropdownMenuItem onClick={() => { logout(); nav("/"); }} data-testid="menu-logout" className="text-red-600">
                    <LogOut size={16} className="mr-2" />{t("logout")}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : (
            <>
              <Button variant="ghost" onClick={() => nav("/auth")} data-testid="header-login" className="rounded-full font-semibold hidden sm:inline-flex">
                {t("login")}
              </Button>
              <Button onClick={() => nav("/auth?mode=signup")} data-testid="header-signup"
                className="rounded-full font-bold bg-primary hover:bg-[#6D28D9] px-3 sm:px-4 text-sm sm:text-base whitespace-nowrap">
                {t("signup")}
              </Button>
            </>
          )}

          {/* Mobile / tablet menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="mobile-menu" className="lg:hidden w-11 h-11 rounded-full border-2 border-stone-200 grid place-items-center text-stone-700">
                <Menu size={20} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="rounded-2xl w-56">
              {links.map((l) => (
                <DropdownMenuItem key={l.to} onClick={() => nav(l.to)} data-testid={`m-nav-${l.to.slice(1)}`} className="font-semibold">
                  {l.label}
                </DropdownMenuItem>
              ))}
              {!user && (
                <DropdownMenuItem onClick={() => nav("/auth")} data-testid="m-login" className="font-semibold">
                  {t("login")}
                </DropdownMenuItem>
              )}
              <div className="px-2 py-2 border-t border-stone-100 mt-1"><LangToggle idSuffix="-m" /></div>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
}
